import { addDays, addMonths, compareLocalDates, endOfMonth, maxLocalDate, monthKey } from './dates';
import { buildForecast, commitmentFor, isInflow } from './forecast';
import {
  absoluteMoney,
  addMoney,
  assertNonNegativeMoney,
  assertPositiveMoney,
  formatMoney,
  subtractMoney,
} from './money';
import type {
  AffordabilityAlternative,
  AffordabilityClassification,
  AffordabilityInput,
  AffordabilityResult,
  CashFlowEvent,
  ForecastInput,
  ForecastResult,
  LocalDate,
  Money,
} from './types';

interface ScenarioEvaluation {
  classification: AffordabilityClassification;
  reasons: string[];
  forecast: ForecastResult;
  immediateBalance: Money;
  immediateAccountBalance: Money;
  goalImpact: Money;
}

const SEVERITY: Record<AffordabilityClassification, number> = {
  SAFE: 0,
  MANAGEABLE_WITH_TRADEOFF: 1,
  HIGH_RISK: 2,
  NOT_CURRENTLY_MANAGEABLE: 3,
};

export function assessAffordability(input: AffordabilityInput): AffordabilityResult {
  assertPositiveMoney(input.proposal.amount, 'Proposed amount');
  assertNonNegativeMoney(input.committedGoalContribution ?? 0, 'Committed goal contribution');
  if (compareLocalDates(input.proposal.date, input.from) < 0) {
    throw new Error('Proposed date precedes the assessment start date');
  }
  const selectedAccount = input.accounts.find(
    (candidate) => candidate.id === input.proposal.accountId,
  );
  if (!selectedAccount) throw new Error('Paying account does not exist');
  if (
    input.proposal.currency &&
    selectedAccount.currency &&
    input.proposal.currency !== selectedAccount.currency
  ) {
    throw new Error('Proposal currency does not match paying account');
  }

  const horizonEnd = maxLocalDate(input.to, defaultAffordabilityHorizon(input.proposal.date));
  const baseInput = forecastInput(input, horizonEnd);
  const baseline = buildForecast(baseInput);
  const evaluation = evaluateCandidate(
    input,
    baseInput,
    baseline,
    input.proposal.amount,
    input.proposal.date,
    input.proposal.accountId,
  );
  const lowerSafeAmount = findLowerSafeAmount(input, baseInput, baseline);
  const saferDates = findSaferDates(input, baseInput, baseline, evaluation.classification);
  const alternativeAccounts = findAlternativeAccounts(
    input,
    baseInput,
    baseline,
    evaluation.classification,
  );

  const evidence = [
    `Accessible headroom immediately after the purchase is ${formatMoney(evaluation.immediateBalance)}.`,
    `The selected account balance immediately after the purchase is ${formatMoney(evaluation.immediateAccountBalance)}.`,
    `The minimum projected accessible headroom is ${formatMoney(evaluation.forecast.minimumAccessibleBalance)} on ${evaluation.forecast.minimumDate}.`,
    `The configured safety buffer is ${formatMoney(input.safetyBuffer)}.`,
  ];
  if (evaluation.forecast.mandatoryShortfall) {
    evidence.push(
      'At least one mandatory obligation cannot be funded from unprotected liquid cash when due.',
    );
  }
  if (evaluation.forecast.accountFundingGap) {
    evidence.push(
      'At least one account needs a deliberate eligible-account transfer before its outflow.',
    );
  }
  if (evaluation.forecast.accountMinimumBreach) {
    evidence.push('At least one account falls below its configured minimum balance.');
  }
  if (evaluation.forecast.safetyBufferBreach) {
    evidence.push('Projected accessible headroom falls below the configured safety buffer.');
  }
  if (evaluation.forecast.protectedAccountUsed || selectedAccount.protected) {
    evidence.push(
      'The scenario uses a protected account; protected money is not assumed spendable.',
    );
  }
  if (evaluation.goalImpact > 0) {
    evidence.push(
      `The scenario increases the gap against committed goal capacity by ${formatMoney(evaluation.goalImpact)}.`,
    );
  }

  return {
    classification: evaluation.classification,
    baseline,
    forecast: evaluation.forecast,
    immediateBalance: evaluation.immediateBalance,
    immediateAccountBalance: evaluation.immediateAccountBalance,
    projectedMonthEndBalance: balanceAtDate(evaluation.forecast, endOfMonth(input.proposal.date)),
    mandatoryObligationCoverage: !evaluation.forecast.mandatoryShortfall,
    protectedReserveUsed: evaluation.forecast.protectedAccountUsed || selectedAccount.protected,
    goalImpact: evaluation.goalImpact,
    evidence,
    classificationReasons: evaluation.reasons,
    lowerSafeAmount,
    saferDates,
    alternativeAccounts,
    assumptions: [
      `Forecast window: ${input.from} through ${horizonEnd}.`,
      'Same-day outflows are processed before expected income.',
      'The importance label does not override cash-flow constraints.',
      'No investment return or unplanned use of protected money is assumed.',
      ...evaluation.forecast.assumptions,
    ],
    horizonEnd,
  };
}

export function defaultAffordabilityHorizon(proposedDate: LocalDate): LocalDate {
  return maxLocalDate(addDays(proposedDate, 60), endOfMonth(addMonths(proposedDate, 1)));
}

function evaluateCandidate(
  source: AffordabilityInput,
  baseInput: ForecastInput,
  baseline: ForecastResult,
  amount: Money,
  date: LocalDate,
  accountId: string,
): ScenarioEvaluation {
  assertNonNegativeMoney(amount, 'Candidate amount');
  const account = source.accounts.find((candidate) => candidate.id === accountId);
  if (!account) throw new Error(`Unknown candidate account: ${accountId}`);
  const proposalEvent = createProposalEvent(source, amount, date, accountId);
  const scenarioEvents = amount === 0 ? baseInput.events : [...baseInput.events, proposalEvent];
  const forecast = buildForecast({ ...baseInput, events: scenarioEvents });
  const proposalPoint =
    amount === 0 ? undefined : forecast.points.find((point) => point.event.id === proposalEvent.id);
  const immediateBalance =
    proposalPoint?.totalAccessibleBalance ?? baseline.openingAccessibleBalance;
  const immediateAccountBalance = proposalPoint?.accountBalances[accountId] ?? account.balance;

  const mandatoryOnlyEvents = baseInput.events.filter(
    (event) => isInflow(event) || commitmentFor(event) === 'MANDATORY',
  );
  if (amount > 0) mandatoryOnlyEvents.push(proposalEvent);
  const mandatoryOnly = buildForecast({ ...baseInput, events: mandatoryOnlyEvents });

  const committedGoalContribution = source.committedGoalContribution ?? 0;
  const baselineGoalGap = Math.max(
    0,
    subtractMoney(committedGoalContribution, baseline.endingSafeToSpend),
  );
  const scenarioGoalGap = Math.max(
    0,
    subtractMoney(committedGoalContribution, forecast.endingSafeToSpend),
  );
  const goalImpact = Math.max(0, subtractMoney(scenarioGoalGap, baselineGoalGap));
  const projectedSurplusTradeoff = crossesMonthlyCashFlowZero(baseInput.events, amount, date);

  const reasons: string[] = [];
  if (
    immediateAccountBalance < 0 ||
    forecast.mandatoryShortfall ||
    mandatoryOnly.minimumUnprotectedLiquidBalance < 0
  ) {
    if (immediateAccountBalance < 0) reasons.push('PROPOSAL_ACCOUNT_NEGATIVE');
    if (forecast.mandatoryShortfall) reasons.push('MANDATORY_OBLIGATION_UNFUNDED');
    if (mandatoryOnly.minimumUnprotectedLiquidBalance < 0) {
      reasons.push('MANDATORY_ONLY_LIQUIDITY_NEGATIVE');
    }
    return {
      classification: 'NOT_CURRENTLY_MANAGEABLE',
      reasons,
      forecast,
      immediateBalance,
      immediateAccountBalance,
      goalImpact,
    };
  }

  if (
    (amount > 0 && account.protected) ||
    forecast.protectedAccountUsed ||
    forecast.accountMinimumBreach ||
    forecast.safetyBufferBreach ||
    forecast.accountFundingGap ||
    forecast.goalReservedBalanceBreach ||
    forecast.minimumUnprotectedLiquidBalance < 0
  ) {
    if (amount > 0 && account.protected) reasons.push('PROTECTED_ACCOUNT_SELECTED');
    if (forecast.protectedAccountUsed) reasons.push('PROTECTED_BALANCE_USED');
    if (forecast.accountMinimumBreach) reasons.push('ACCOUNT_MINIMUM_BREACHED');
    if (forecast.safetyBufferBreach) reasons.push('SAFETY_BUFFER_BREACHED');
    if (forecast.accountFundingGap) reasons.push('ACCOUNT_FUNDING_ACTION_REQUIRED');
    if (forecast.goalReservedBalanceBreach) reasons.push('GOAL_RESERVED_BALANCE_BREACHED');
    if (forecast.minimumUnprotectedLiquidBalance < 0) {
      reasons.push('CURRENT_PLAN_REQUIRES_NONMANDATORY_CHANGE');
    }
    return {
      classification: 'HIGH_RISK',
      reasons,
      forecast,
      immediateBalance,
      immediateAccountBalance,
      goalImpact,
    };
  }

  if (scenarioGoalGap > 0 || projectedSurplusTradeoff) {
    if (scenarioGoalGap > 0) reasons.push('GOAL_CONTRIBUTION_GAP');
    if (projectedSurplusTradeoff) reasons.push('PROJECTED_MONTH_CASH_FLOW_TURNS_NEGATIVE');
    return {
      classification: 'MANAGEABLE_WITH_TRADEOFF',
      reasons,
      forecast,
      immediateBalance,
      immediateAccountBalance,
      goalImpact,
    };
  }

  return {
    classification: 'SAFE',
    reasons: ['ALL_CONFIGURED_CONSTRAINTS_PRESERVED'],
    forecast,
    immediateBalance,
    immediateAccountBalance,
    goalImpact,
  };
}

function findLowerSafeAmount(
  source: AffordabilityInput,
  baseInput: ForecastInput,
  baseline: ForecastResult,
): Money {
  const zero = evaluateCandidate(
    source,
    baseInput,
    baseline,
    0,
    source.proposal.date,
    source.proposal.accountId,
  );
  if (zero.classification !== 'SAFE') return 0;
  const proposed = evaluateCandidate(
    source,
    baseInput,
    baseline,
    source.proposal.amount,
    source.proposal.date,
    source.proposal.accountId,
  );
  if (proposed.classification === 'SAFE') return source.proposal.amount;

  let low: Money = 0;
  let high: Money = source.proposal.amount;
  while (low < high) {
    const midpoint = addMoney(low, Math.ceil((high - low) / 2));
    const candidate = evaluateCandidate(
      source,
      baseInput,
      baseline,
      midpoint,
      source.proposal.date,
      source.proposal.accountId,
    );
    if (candidate.classification === 'SAFE') low = midpoint;
    else high = subtractMoney(midpoint, 1);
  }
  return low;
}

function findSaferDates(
  source: AffordabilityInput,
  baseInput: ForecastInput,
  baseline: ForecastResult,
  current: AffordabilityClassification,
): AffordabilityAlternative[] {
  const incomeBoundaryDates = baseInput.events
    .filter(isInflow)
    .map((event) => event.scheduledDate ?? event.dueWindowEnd ?? event.date)
    .filter((date): date is LocalDate => date !== undefined)
    .map((date) => addDays(date, 1));
  const candidateDates = [...(source.candidateDates ?? []), ...incomeBoundaryDates]
    .filter(
      (date) =>
        compareLocalDates(date, source.proposal.date) > 0 &&
        compareLocalDates(date, baseInput.to) <= 0,
    )
    .sort(compareLocalDates);

  const alternatives: AffordabilityAlternative[] = [];
  for (const date of [...new Set(candidateDates)]) {
    const candidate = evaluateCandidate(
      source,
      baseInput,
      baseline,
      source.proposal.amount,
      date,
      source.proposal.accountId,
    );
    if (SEVERITY[candidate.classification] >= SEVERITY[current]) continue;
    alternatives.push({
      kind: 'SAFER_DATE',
      classification: candidate.classification,
      amount: source.proposal.amount,
      date,
      accountId: source.proposal.accountId,
    });
    if (alternatives.length === 3) break;
  }
  return alternatives;
}

function findAlternativeAccounts(
  source: AffordabilityInput,
  baseInput: ForecastInput,
  baseline: ForecastResult,
  current: AffordabilityClassification,
): AffordabilityAlternative[] {
  const alternatives: AffordabilityAlternative[] = [];
  for (const account of source.accounts) {
    if (
      account.id === source.proposal.accountId ||
      account.active === false ||
      !account.spendable ||
      account.protected ||
      (source.proposal.currency &&
        account.currency &&
        source.proposal.currency !== account.currency)
    ) {
      continue;
    }
    const candidate = evaluateCandidate(
      source,
      baseInput,
      baseline,
      source.proposal.amount,
      source.proposal.date,
      account.id,
    );
    if (SEVERITY[candidate.classification] >= SEVERITY[current]) continue;
    alternatives.push({
      kind: 'ANOTHER_ACCOUNT',
      classification: candidate.classification,
      amount: source.proposal.amount,
      date: source.proposal.date,
      accountId: account.id,
    });
  }
  return alternatives.sort(
    (left, right) =>
      SEVERITY[left.classification] - SEVERITY[right.classification] ||
      left.accountId.localeCompare(right.accountId),
  );
}

function forecastInput(input: AffordabilityInput, to: LocalDate): ForecastInput {
  return {
    accounts: input.accounts,
    events: input.events,
    from: input.from,
    to,
    safetyBuffer: input.safetyBuffer,
    ...(input.includeProtected === undefined ? {} : { includeProtected: input.includeProtected }),
  };
}

function createProposalEvent(
  source: AffordabilityInput,
  amount: Money,
  date: LocalDate,
  accountId: string,
): CashFlowEvent {
  return {
    id: `__proposal__:${accountId}:${date}:${amount}`,
    date,
    label: source.proposal.description || 'Proposed purchase',
    amount,
    accountId,
    sourceAccountId: accountId,
    kind: 'EXPENSE',
    commitmentLevel: 'DISCRETIONARY',
    isProposal: true,
    ...(source.proposal.currency ? { currency: source.proposal.currency } : {}),
  };
}

function crossesMonthlyCashFlowZero(
  events: readonly CashFlowEvent[],
  proposalAmount: Money,
  proposalDate: LocalDate,
): boolean {
  let baselineNet: Money = 0;
  let hasExpectedIncome = false;
  const month = monthKey(proposalDate);
  for (const event of events) {
    const eventDate = event.scheduledDate ?? event.date ?? event.dueWindowEnd;
    if (!eventDate || monthKey(eventDate) !== month || event.settled) continue;
    const magnitude = absoluteMoney(event.amount);
    if (event.kind === 'INCOME' || event.kind === 'DEBT_INCOME') {
      hasExpectedIncome = true;
      baselineNet = addMoney(baselineNet, magnitude);
    } else if (event.kind === 'EXPENSE' || event.kind === 'DEBT_EXPENSE') {
      baselineNet = subtractMoney(baselineNet, magnitude);
    }
  }
  return hasExpectedIncome && baselineNet >= 0 && subtractMoney(baselineNet, proposalAmount) < 0;
}

function balanceAtDate(forecast: ForecastResult, date: LocalDate): Money {
  const eligible = forecast.points.filter(
    (point) => compareLocalDates(point.effectiveDate, date) <= 0,
  );
  return eligible.at(-1)?.totalAccessibleBalance ?? forecast.openingAccessibleBalance;
}
